import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Button, IconButton, Grid, CircularProgress, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { componentService } from '@/services';
import type { SensorCamera } from '@/services';
import { required } from '@core/utils/validation.ts';
import { ZoneGate } from '@core/types';
import FormSelect from '@/components/form/FormSelect.tsx';
import FieldWithIcon from '@/components/form/FieldWithIcon.tsx';
import { WcnCheckbox } from '@/components';

interface ZoneGatesFieldsProps {
    value: ZoneGate[];
    shopId: string;
    zoneType?: number;
    onChange: (gates: ZoneGate[]) => void;
}

const ZoneGatesFields: React.FC<ZoneGatesFieldsProps> = ({ value, shopId, zoneType, onChange }) => {
    const { t } = useTranslation();
    const [zoneGates, setZoneGates] = useState<ZoneGate[]>(value);
    const [cameras, setCameras] = useState<SensorCamera[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadSensorCameras = async () => {
            setLoading(true);
            try {
                const response = await componentService.loadSensorCamerasForShop(shopId);
                setCameras(response);
            } catch (error: any) {
                console.error('Failed to load sensor cameras', error);
            } finally {
                setLoading(false);
            }
        };

        if (shopId) {
            loadSensorCameras();
        }
    }, [shopId, t]);

    useEffect(() => {
        setZoneGates(value);
    }, [value]);

    const getCameraLinesOrZones = useMemo(() => {
        return (sensorCameraId: string | null) => {
            if (!sensorCameraId) return [];
            const camera = cameras.find((c) => c.id === sensorCameraId);
            if (!camera) return [];

            switch (zoneType) {
                case 1: // virtual
                    return camera.lines || [];
                case 2: // native
                    return camera.zones || [];
                default:
                    return [];
            }
        };
    }, [cameras, zoneType]);

    const handleGateChange = useCallback(
        (index: number, field: keyof ZoneGate, value: any) => {
            setZoneGates((prev) => {
                const updated = [...prev];
                updated[index] = { ...updated[index], [field]: value };
                onChange(updated);

                return updated;
            });
        },
        [onChange]
    );

    const handleCameraChange = useCallback(
        (index: number, value: any) => {
            setZoneGates((prev) => {
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    sensorCameraId: value,
                    elementId: null
                };
                onChange(updated);
                return updated;
            });
        },
        [onChange]
    );

    const handleBackupCameraChange = useCallback(
        (index: number, value: any) => {
            setZoneGates((prev) => {
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    backupSensorCameraId: value,
                    backupElementId: null
                };
                onChange(updated);
                return updated;
            });
        },
        [onChange]
    );

    const handleAddGate = useCallback(() => {
        setZoneGates((prev) => {
            const updated = [
                ...prev,
                {
                    sensorCameraId: null,
                    elementId: null,
                    isReversed: false
                }
            ];
            onChange(updated);
            return updated;
        });
    }, [onChange]);

    const handleDeleteGate = useCallback(
        (index: number) => {
            setZoneGates((prev) => {
                const updated = prev.filter((_, i) => i !== index);
                onChange(updated);
                return updated;
            });
        },
        [onChange]
    );

    const getTransformedItems = useCallback(
        (sensorCameraId: string | null) => {
            const items = getCameraLinesOrZones(sensorCameraId);
            return items.map((item) => ({ value: item }));
        },
        [getCameraLinesOrZones]
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {zoneGates.map((zoneGate, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <FieldWithIcon
                            select={
                                <FormSelect
                                    name={`sensorCameraId_${index}`}
                                    label={t('sensorCamera')}
                                    value={zoneGate.sensorCameraId || ''}
                                    items={cameras}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(value) => handleCameraChange(index, value)}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                    placeholder="-"
                                />
                            }
                            icon={
                                !zoneGate.sensorCameraId && index > 0 ? (
                                    <IconButton sx={{ fontSize: 18 }} onClick={() => handleDeleteGate(index)}>
                                        <i className="bi bi-trash3" />
                                    </IconButton>
                                ) : null
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {zoneGate.sensorCameraId && (
                            <FieldWithIcon
                                select={
                                    <FormSelect
                                        name={`zoneGate_${index}`}
                                        label={zoneType === 1 ? t('line') : t('zone')}
                                        value={zoneGate?.elementId || ''}
                                        items={getTransformedItems(zoneGate.sensorCameraId)}
                                        itemText="value"
                                        itemValue="value"
                                        onChange={(value) => handleGateChange(index, 'elementId', value)}
                                        rules={[required(t)]}
                                        required
                                        sx={{ width: '100%' }}
                                        placeholder="-"
                                    />
                                }
                                icon={
                                    index > 0 ? (
                                        <IconButton sx={{ fontSize: 18 }} onClick={() => handleDeleteGate(index)}>
                                            <i className="bi bi-trash3" />
                                        </IconButton>
                                    ) : null
                                }
                            />
                        )}
                    </Grid>
                    {zoneGate.sensorCameraId && (
                        <>
                            <Grid item xs={12} md={6}>
                                <FormSelect
                                    name={`sensorCamera_fallback_${index}`}
                                    label={t('optionalFallbackCamera')}
                                    value={zoneGate.backupSensorCameraId || ''}
                                    items={cameras.filter((c) => c.id !== zoneGate.sensorCameraId)}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(value) => handleBackupCameraChange(index, value)}
                                    sx={{ width: '100%' }}
                                    placeholder={t('-')}
                                />
                            </Grid>
                            {zoneGate.backupSensorCameraId && (
                                <Grid item xs={12} md={6}>
                                    <FieldWithIcon
                                        select={
                                            <FormSelect
                                                name={`zoneGate_fallback_${index}`}
                                                label={zoneType === 1 ? t('line') : t('zone')}
                                                value={zoneGate?.backupElementId || ''}
                                                items={getTransformedItems(zoneGate.backupSensorCameraId || '')}
                                                itemText="value"
                                                itemValue="value"
                                                onChange={(value) => handleGateChange(index, 'backupElementId', value)}
                                                rules={[required(t)]}
                                                required
                                                sx={{ width: '100%' }}
                                            />
                                        }
                                        icon={
                                            <IconButton
                                                onClick={() => handleBackupCameraChange(index, null)}
                                                sx={{ fontSize: 18 }}
                                            >
                                                <i className="bi bi-trash3" />
                                            </IconButton>
                                        }
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                    {zoneGate.sensorCameraId && zoneType === 1 && (
                        <Grid item xs={12} md={12}>
                            <WcnCheckbox
                                label={t('countBackwards')}
                                checked={zoneGate.isReversed}
                                onChange={(e) => handleGateChange(index, 'isReversed', e.target.checked)}
                                className="wcn-checkbox--16"
                            />
                        </Grid>
                    )}
                </Grid>
            ))}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mt: 1 }}>
                <Button
                    variant="text"
                    startIcon={<i className="bi bi-plus" />}
                    onClick={handleAddGate}
                    sx={{ textTransform: 'none' }}
                >
                    {t('addSensorCamera')}
                </Button>
            </Box>
        </Box>
    );
};

export default ZoneGatesFields;
